import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CalendarDays, Building2, FileSignature, Search } from 'lucide-react';
import type { DashboardSummary } from '@aimmo/contracts';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  Skeleton,
} from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { useAgencySession } from '../lib/session.js';
import { formatTime } from '../lib/format.js';
import { queryKeys } from '../lib/queries.js';

interface StatCardProps {
  title: string;
  icon: typeof CalendarDays;
  value: number;
  caption: string;
  /** Shown as a warning chip when non-zero: the number that asks for attention. */
  alert?: { count: number; label: string };
  to: string;
}

function StatCard({ title, icon: Icon, value, caption, alert, to }: StatCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className="size-4 text-muted" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-display text-ink">{value}</span>
          <span className="text-secondary text-muted">{caption}</span>
        </div>
        {alert && alert.count > 0 && (
          <Badge tone="warning">{`${alert.count} ${alert.label}`}</Badge>
        )}
        <Button asChild variant="ghost" size="sm" className="justify-self-start">
          <Link to={to}>
            Apri
            <ArrowRight />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function AppointmentsCard({ data }: { data: DashboardSummary['appointmentsToday'] }) {
  return (
    <Card className="sm:col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="size-4 text-muted" />
          Appuntamenti di oggi
        </CardTitle>
        <CardDescription>
          {data.total === 0
            ? 'Nessun appuntamento in agenda oggi.'
            : `${data.total} in agenda, i prossimi qui sotto.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        {data.next.map((appointment) => (
          <div
            key={appointment.id}
            className="grid grid-cols-[auto_1fr] items-baseline gap-3 rounded-control border border-border px-3 py-2"
          >
            <span className="text-body font-medium text-ink tabular-nums">
              {appointment.scheduledAt ? formatTime(appointment.scheduledAt) : '—'}
            </span>
            <span className="grid gap-0.5">
              <span className="text-body text-ink">
                {appointment.title ?? appointment.appointmentTypeLabel ?? 'Appuntamento'}
              </span>
              <span className="text-secondary text-muted">{appointment.subjectLabel}</span>
            </span>
          </div>
        ))}
        <Button asChild variant="ghost" size="sm" className="justify-self-start">
          <Link to="/agenda">
            Apri l’agenda
            <ArrowRight />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * The landing page (U1). Every card is a count plus the way to the screen that
 * owns it — the dashboard reports, it is never a second place to work.
 */
export function DashboardPage() {
  const session = useAgencySession();

  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: async ({ signal }) =>
      assertOk(await api.dashboard.summary({ fetchOptions: { signal } })).body,
  });

  return (
    <section className="flex flex-col gap-[var(--card-gap)]">
      <PageHeader title="Dashboard" description={`${session.agency.name} · ${session.user.name}`} />

      {isPending && <Skeleton className="h-64 w-full" />}

      {isError && (
        <Card>
          <CardHeader>
            <CardTitle>Numeri non disponibili</CardTitle>
            <CardDescription>
              Il riepilogo non ha risposto. Le singole schermate restano raggiungibili dal menù.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {data && (
        <div className="grid gap-[var(--card-gap)] sm:grid-cols-2 xl:grid-cols-3">
          <AppointmentsCard data={data.appointmentsToday} />

          <StatCard
            title="Promemoria"
            icon={CalendarDays}
            value={data.reminders.open}
            caption="aperti"
            alert={{ count: data.reminders.overdue, label: 'scaduti' }}
            to="/agenda"
          />

          <StatCard
            title="Incarichi"
            icon={FileSignature}
            value={data.mandates.active}
            caption="attivi"
            alert={{ count: data.mandates.expiringIn30Days, label: 'in scadenza entro 30 giorni' }}
            to="/incarichi"
          />

          <StatCard
            title="Richieste"
            icon={Search}
            value={data.demands.open}
            caption="aperte"
            alert={{ count: data.demands.withoutMatches, label: 'senza alcun match' }}
            to="/richieste"
          />

          <StatCard
            title="Immobili"
            icon={Building2}
            value={data.properties.available}
            caption="disponibili"
            alert={{ count: data.properties.underOffer, label: 'sotto proposta' }}
            to="/immobili"
          />
        </div>
      )}
    </section>
  );
}
